import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import PageBanner from "../Components/PageBanner";
import { getAgentsData } from "../Constants/Data";

export default function Team() {
  const [agentsData, setAgentsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

useEffect(() => {
  const fetchData = async () => {
    try {
      const data = await getAgentsData();
      if (Array.isArray(data)) {
        // Filter out agents with role_id 1, 2 and inactive/deactive status
        const filteredAgents = data.filter(
          (agent) =>
            agent.role_id !== 1 &&
            agent.role_id !== 2 &&
            agent.status !== "inactive" && // Add this condition
            agent.status !== "deactive"
        );

        // Sort agents based on designation priority
        const sortedAgents = sortAgentsByDesignation(filteredAgents);
        setAgentsData(sortedAgents);
      } else {
        setAgentsData([]);
      }
    } catch (error) {
      console.error("Error fetching agent data:", error);
      setAgentsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  fetchData();
}, []);

  // Function to sort agents by designation in specific order
  const sortAgentsByDesignation = (agents) => {
    // Define the order of designations
    const designationOrder = {
      "Chief Operating Officer": 1,
      "Operational Manager": 2,
      "Sales Associate": 3,
      "Senior Sales Agent": 4,
      "Assistant Sales Agent": 5,
      "Property Management Assistant": 6,
    };

    return [...agents].sort((a, b) => {
      const aDesignation = (a.designation || "").trim();
      const bDesignation = (b.designation || "").trim();

      const aOrder = designationOrder[aDesignation] || 999; // Assign high number for unknown designations
      const bOrder = designationOrder[bDesignation] || 999;

      // If same designation, sort alphabetically by first name
      if (aOrder === bOrder) {
        const aName = `${a.firstname || ""} ${a.lastname || ""}`.toLowerCase();
        const bName = `${b.firstname || ""} ${b.lastname || ""}`.toLowerCase();
        return aName.localeCompare(bName);
      }

      return aOrder - bOrder;
    });
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Team | Urban View Real Estate PVT LTD</title>
        <meta
          name="description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="keywords"
          content="Affordable Homes for Sale in South Australia, Affordable Homes for Sale in Melbourne, Affordable Homes for Sale in Queensland, Development Sites for Sale in Sydney, Cheap Rent a Home in Penrith, House Buying in Western Australia, Cheap Duplex Home in Tamworth, Affordable duplex Home in Armidale, Houses for Rental in St Marys, Investement Property in Queensland, Best Investment Property Sale in Brisbane, Best Investment Property Sale in Rouse Hill, Box Hill, Marsden Park, Vineyard, Investment Property Buy in Austral, Investment Property Buy in Tallawong, Investment Property Buy in Oran Park, Investment Property Buy in Leppington"
        />

        <meta name="author" content="Urban View Real Estate PVT LTD" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urbanviewre.com/team" />

        {/* Open Graph */}
        <meta property="og:title" content="Urban View Real Estate PVT LTD" />
        <meta
          property="og:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta property="og:site_name" content="Urban View Real Estate" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urbanviewre.com" />
        <meta
          property="og:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="twitter:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="twitter:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />
        <meta name="twitter:site" content="@urbanviewre" />
        <meta name="twitter:creator" content="@urbanviewre" />

        {/* Facebook  */}
        <meta
          property="fb:admins"
          content="https://www.facebook.com/profile.php?id=61559035348971"
        />

        {/* Instagram */}
        <meta name="instagram:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="instagram:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta name="instagram:site" content="@urbanviewrealestate" />
      </Helmet>

      <PageBanner
        title="Team"
        breadcrumbs={[
          { label: "Home", href: "/", icon: "bi-house-door-fill" },
          { label: "Team", active: true },
        ]}
      />

      <section className="container mb-5 py-5">
        <div className="row mt-4 d-flex justify-content-center">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading our team...</p>
            </div>
          ) : agentsData.length > 0 ? (
            agentsData.map((agent) => {
              // Generate slug for the agent
              const agentSlug =
                agent.firstname && agent.lastname
                  ? `${agent.firstname}-${agent.lastname}`
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                  : `agent-${agent.id}`;

              return (
                <div
                  className="col-12 col-md-6 col-lg-3 d-flex justify-content-center mb-5"
                  key={agent.id || `${agent.firstname}-${agent.lastname}`}
                >
                  <div className="card text-center w-100 rounded-0 agent-card h-100">
                    {agent.profileimg ? (
                      <div className="ratio ratio-1x1 image-container overflow-hidden">
                        <img
                          src={agent.profileimg}
                          alt={agent.firstname || "Agent"}
                          className="card-img-top object-fit-cover"
                        />
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center bg-light ratio ratio-1x1">
                        <span className="text-muted">No Image</span>
                      </div>
                    )}

                    <div className="card-body d-flex flex-column">
                      {/* Hide ENTIRE name section if designation is Property Management Assistant */}
                      {agent.designation !==
                        "Property Management Assistant" && (
                        <Link
                          to={`/team-details/${agentSlug}`}
                          className="text-decoration-none text-dark"
                          aria-label={`View profile of ${
                            agent.firstname || "this agent"
                          } ${agent.lastname || ""}`}
                        >
                          <h5 className="card-title fw-bold mb-1">
                            {agent.firstname || "Unknown"}{" "}
                            {agent.lastname || ""}
                          </h5>
                        </Link>
                      )}

                      <p className="text-primary fw-semibold mb-3">
                        {agent.designation || "No Designation"}
                      </p>

                      {agent.social_media && (
                        <div className="mt-auto d-flex justify-content-center gap-3">
                          {agent.social_media.facebook && (
                            <a
                              href={agent.social_media.facebook}
                              className="text-dark fs-5"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="bi bi-facebook"></i>
                            </a>
                          )}

                          {agent.social_media.twitter && (
                            <a
                              href={agent.social_media.twitter}
                              className="text-dark fs-5"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="bi bi-twitter"></i>
                            </a>
                          )}

                          {agent.social_media.linkedin && (
                            <a
                              href={agent.social_media.linkedin}
                              className="text-dark fs-5"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="bi bi-linkedin"></i>
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-5">
              <p className="lead">No team members found.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
